import { useState, useCallback, useEffect } from 'react'
import {
    injectTourStyles,
    cleanupTourElements,
    createTourPopover,
    positionPopover,
    createTourOverlay,
    TourDemoType,
} from '@/utils/tour-styles'

interface TourStep {
    selector: string
    title: string
    description: string
    demo?: TourDemoType
}

const TOUR_STEPS: TourStep[] = [
    {
        selector: '[data-tour="employee-list"]',
        title: 'Quản lý phòng ban',
        description:
            'Trang quản lý cơ cấu tổ chức.\n• Danh sách phòng ban\n• Quản lý vị trí/chức vụ\n• Phân bổ nhân sự',
    },
    {
        selector: '[data-tour="employee-tabs"]',
        title: 'Chuyển đổi danh sách',
        description:
            'Chuyển đổi giữa các tab:\n• Danh sách nhân viên - xem tất cả nhân viên\n• Phòng ban - quản lý cơ cấu tổ chức',
    },
    {
        selector: '[data-tour="create-department-btn"]',
        title: 'Tạo phòng ban mới',
        description:
            'Click để tạo phòng ban mới.\n• Nhập tên phòng ban\n• Thiết lập thông tin cơ bản\n• Thêm các vị trí sau khi tạo',
    },
    {
        selector: '[data-tour="department-filters"]',
        title: 'Bộ lọc phòng ban',
        description:
            'Lọc phòng ban theo trạng thái:\n• Đang hoạt động\n• Ngừng hoạt động',
    },
    {
        selector: '[data-tour="department-data-table"]',
        title: 'Danh sách phòng ban',
        description:
            'Bảng dữ liệu phòng ban:\n• Xem chi tiết từng phòng ban\n• Chỉnh sửa thông tin\n• Xoá phòng ban (nếu không còn nhân viên)\n• Export Excel',
    },
]

/**
 * Tour hook for Department List page
 * Shows key features of the department management interface
 */
export const useDepartmentListTour = () => {
    const [isActive, setIsActive] = useState(false)
    const [currentStep, setCurrentStep] = useState(0)

    const getVisibleSteps = useCallback(() => {
        return TOUR_STEPS.filter((step) => {
            const element = document.querySelector(step.selector)
            return element !== null
        })
    }, [])

    const endTour = useCallback(() => {
        cleanupTourElements()
        setIsActive(false)
        setCurrentStep(0)
    }, [])

    const showTourStep = useCallback(
        (stepIndex: number, steps: TourStep[]) => {
            injectTourStyles()
            cleanupTourElements()
            createTourOverlay()

            const step = steps[stepIndex]
            if (!step) return

            const element = document.querySelector(step.selector) as HTMLElement
            if (!element) {
                // Skip to next step if element not found
                if (stepIndex < steps.length - 1) {
                    showTourStep(stepIndex + 1, steps)
                } else {
                    endTour()
                }
                return
            }

            element.classList.add('tour-highlight-element')
            element.scrollIntoView({ behavior: 'smooth', block: 'center' })

            setTimeout(() => {
                const isLastStep = stepIndex === steps.length - 1
                const popover = createTourPopover({
                    title: step.title,
                    description: step.description,
                    currentStep: stepIndex + 1,
                    totalSteps: steps.length,
                    onPrev:
                        stepIndex > 0
                            ? () => goToStep(stepIndex - 1, steps)
                            : undefined,
                    onNext: isLastStep
                        ? endTour
                        : () => goToStep(stepIndex + 1, steps),
                    onClose: endTour,
                    isLastStep,
                    demo: step.demo,
                })

                document.body.appendChild(popover)
                positionPopover(popover, element)
            }, 300)
        },
        [endTour]
    )

    const goToStep = useCallback(
        (stepIndex: number, steps: TourStep[]) => {
            setCurrentStep(stepIndex)
            showTourStep(stepIndex, steps)
        },
        [showTourStep]
    )

    const startTour = useCallback(() => {
        const visibleSteps = getVisibleSteps()
        if (visibleSteps.length === 0) {
            console.warn('No visible tour elements found')
            return
        }

        setIsActive(true)
        setCurrentStep(0)

        // Scroll to top first
        window.scrollTo({ top: 0, behavior: 'smooth' })

        // Start tour after scroll
        setTimeout(() => {
            showTourStep(0, visibleSteps)
        }, 300)
    }, [getVisibleSteps, showTourStep])

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            cleanupTourElements()
        }
    }, [])

    return {
        isActive,
        currentStep,
        startTour,
        endTour,
        totalSteps: getVisibleSteps().length,
    }
}

export default useDepartmentListTour
